<template>
  <div class="card p-4 shadow-lg rounded-2xl">
    <h5 class="mb-4 font-bold text-xl">Update Personal Information</h5>

    <form @submit.prevent="updateInfo">
      <!-- row: first / last / middle -->
      <div class="row">
        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <label class="form-label"
            >First Name <span class="text-danger">*</span></label
          >
          <input
            v-model="form.first_name"
            type="text"
            class="form-control"
            required
          />
        </div>
        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <label class="form-label"
            >Last Name <span class="text-danger">*</span></label
          >
          <input
            v-model="form.last_name"
            type="text"
            class="form-control"
            required
          />
        </div>
        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <label class="form-label">Middle Name (if any)</label>
          <input
            v-model="form.middle_initial"
            type="text"
            class="form-control"
          />
        </div>
      </div>

      <!-- row: dob / gender / civil status -->
      <div class="row">
        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <label class="form-label"
            >Date of Birth <span class="text-danger">*</span></label
          >
          <input
            v-model="form.date_of_birth"
            type="date"
            class="form-control"
            required
          />
        </div>
        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <label class="form-label"
            >Gender <span class="text-danger">*</span></label
          >
          <select v-model="form.gender" class="form-select">
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>
        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <label class="form-label"
            >Civil Status <span class="text-danger">*</span></label
          >
          <select v-model="form.civil_status" class="form-select">
            <optgroup label="Single / Married">
              <option value="Single">Single</option>
              <option value="Married">Married</option>
            </optgroup>
            <optgroup label="Other Filing Status">
              <option value="HeadOfHouse">Head of Household</option>
            </optgroup>
          </select>
          <small
            v-if="form.civil_status == 'HeadOfHouse'"
            class="form-text text-muted"
          >
            Select <b>Head of Household</b> only if you’re unmarried and pay
            more than half the costs of keeping up a home for yourself and a
            qualifying individual.
          </small>
        </div>

        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <label class="form-label"
            >Citizenship or Immigration Status
            <span class="text-danger">*</span></label
          >
          <select v-model="form.citizenship" class="form-select">
            <option value="us-citizen">A citizen of the United States</option>
            <option value="non-us-citizen">
              A noncitizen national of the United States
            </option>
            <option value="lawful-resident">A lawful permanent resident</option>
            <option value="alien">An alien authorized to work until</option>
          </select>
        </div>
        <div
          class="col-lg-4 col-md-6 col-12 mb-3"
          v-if="form.citizenship == 'lawful-resident'"
        >
          <label class="form-label"
            >USCIS or A-Number <span class="text-danger">*</span></label
          >
          <input
            v-model="form.citizenship_uscis_or_a_number"
            type="text"
            class="form-control"
          />
        </div>
        <div
          class="col-lg-4 col-md-6 col-12 mb-3"
          v-if="form.citizenship == 'alien'"
        >
          <label class="form-label"
            >Expiry Date: <span class="text-danger">*</span></label
          >
          <input
            v-model="form.citizenship_id_expiry_date"
            type="date"
            class="form-control"
          />
        </div>
        <div
          class="col-lg-4 col-md-6 col-12 mb-3"
          v-if="form.citizenship == 'alien'"
        >
          <label class="form-label">Number (if any)</label>
          <input v-model="form.citizenship_number" class="form-control" />
          <small class="form-text text-muted">
            USCIS A-Number / Form I-94 Admission Number / Foreign Passport
            Number and Country of Issuance
          </small>
        </div>
      </div>

      <!-- row: ss / contact / phone -->
      <div class="row">
        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <label class="form-label"
            >SS Number <span class="text-danger">*</span></label
          >
          <input
            v-model="form.ss_number"
            v-mask="'###-##-####'"
            type="text"
            class="form-control"
          />
        </div>
        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <label class="form-label">Mobile Number (if any)</label>
          <input
            v-model="form.contact_number"
            v-mask="'###-###-####'"
            type="tel"
            class="form-control"
          />
        </div>
        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <label class="form-label">Phone Number (if any)</label>
          <input
            v-model="form.phone_number"
            v-mask="'+1 (###) ###-####'"
            type="tel"
            class="form-control"
          />
        </div>
      </div>

      <!-- row: email / username (2 columns) -->
      <div class="row">
        <div class="col-lg-8 col-md-12 col-12 mb-3">
          <label class="form-label"
            >Email <span class="text-danger">*</span></label
          >
          <input
            v-model="form.email"
            type="email"
            class="form-control"
            required
          />
        </div>
      </div>

      <!-- Address: street (full), city/state/zip -->
      <h6 class="mt-3 mb-2 font-semibold">
        Address <span class="text-danger">*</span>
      </h6>
      <div class="row">
        <div class="col-12 mb-3">
          <label class="form-label"
            >Street Address <span class="text-danger">*</span></label
          >
          <input v-model="form.street" type="text" class="form-control" />
        </div>

        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <label class="form-label"
            >City or Town <span class="text-danger">*</span></label
          >
          <input v-model="form.city" type="text" class="form-control" />
        </div>
        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <label class="form-label"
            >State <span class="text-danger">*</span></label
          >
          <select v-model="form.state" class="form-select">
            <option value="California">California</option>
            <option value="Texas">Texas</option>
            <option value="New York">New York</option>
            <option value="Florida">Florida</option>
            <option value="Illinois">Illinois</option>
          </select>
        </div>
        <div class="col-lg-4 col-md-6 col-12 mb-3">
          <label class="form-label"
            >Zip Code <span class="text-danger">*</span></label
          >
          <input v-model="form.zip" type="text" class="form-control" />
        </div>
      </div>

      <!-- submit -->
      <div class="mt-4 text-end">
        <button
          type="submit"
          class="btn bg-gradient-success text-white px-5 py-2 rounded-xl shadow-md"
        >
          Save Changes
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  data() {
    return {
      form: {
        first_name: "",
        last_name: "",
        middle_initial: "",
        date_of_birth: "",
        gender: "Male",
        ss_number: "",
        contact_number: "",
        phone_number: "",
        email: "",
        street: "",
        city: "",
        state: "California",
        civil_status: "",
        zip: "",
        citizenship: "",
        citizenship_uscis_or_a_number: "",
        citizenship_id_expiry_date: "",
        citizenship_number: "",
      },
    };
  },
  methods: {
    validateForm() {
      const requiredKeys = [
        "first_name",
        "last_name",
        "date_of_birth",
        "gender",
        "ss_number",
        "email",
        "street",
        "city",
        "state",
        "civil_status",
        "zip",
        "citizenship",
      ];
      if (this.form.citizenship === "lawful-resident") {
        requiredKeys.push("citizenship_uscis_or_a_number");
      }
      if (this.form.citizenship === "alien") {
        requiredKeys.push("citizenship_id_expiry_date");
      }
      const missingFields = [];

      for (const key of requiredKeys) {
        const value = this.form[key];

        const isEmpty =
          value === null ||
          value === undefined ||
          (typeof value === "string" && value.trim() === "") ||
          (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
          missingFields.push(this.formatLabel(key));
        }
      }

      if (missingFields.length > 0) {
        Swal.fire(
          "Validation Error",
          `${missingFields.join(", ")} ${
            missingFields.length > 1 ? "are" : "is"
          } required`,
          "warning"
        );
        return false;
      }
      return true;
    },

    formatLabel(key) {
      return key
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim();
    },

    async updateInfo() {
      if (!this.validateForm()) return;

      try {
        Swal.fire({
          title: "Uploading...",
          text: "Please wait while the records is being updated.",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/update-employee-data`,
          this.form,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        this.$emit("personal-form-updated");
        Swal.fire(
          "Success",
          "Personal information updated successfully",
          "success"
        );
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Update failed. Please try again.", "error");
      }
    },

    async fetchEmployeeData() {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch-employee-data`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (res.data.success) {
          // Load only fields editable on this tab; other API fields are not form inputs.
          for (const key of Object.keys(this.form)) {
            if (Object.prototype.hasOwnProperty.call(res.data.data, key)) {
              this.form[key] = res.data.data[key];
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch employee data", err);
        Swal.fire("Error", "Could not fetch employee data", "error");
      }
    },
  },
  mounted() {
    this.fetchEmployeeData();
  },
};
</script>
